import { useState, useCallback } from 'react';

interface Position {
  latitude: number;
  longitude: number;
}

interface UseGeolocationReturn {
  position: Position | null;
  error: string | null;
  isLoading: boolean;
  getCurrentPosition: () => void;
}

/**
 * Hook to get user's current geolocation
 */
export function useGeolocation(): UseGeolocationReturn {
  const [position, setPosition] = useState<Position | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getCurrentPosition = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setIsLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPosition({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
        setIsLoading(false);
      },
      (err) => {
        setError(err.message || 'Unable to retrieve your location');
        setIsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, []);

  return {
    position,
    error,
    isLoading,
    getCurrentPosition,
  };
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * Returns distance in miles
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Geocode an address to get coordinates
 * This is a placeholder - in production, you'd use a geocoding service like Google Maps Geocoding API
 */
export async function geocodeAddress(address: string): Promise<Position | null> {
  // For now, return null - addresses will need to be geocoded via API
  // In production, integrate with Google Maps Geocoding API or similar
  return null;
}





